import { useEffect, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Once-only editorial entrance for [data-reveal] (single element) and
 * [data-reveal-stagger] (children animate in sequence).
 * - Nothing is hidden by CSS: content stays visible if JS fails.
 * - Elements already in view at setup are left untouched (no flash).
 * - Skipped entirely for prefers-reduced-motion.
 */
export default function useReveal(rootRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const dist = coarse ? 10 : 16;

    const ctx = gsap.context(() => {
      const vh = window.innerHeight;
      const groups: HTMLElement[][] = [];
      root.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => groups.push([el]));
      root.querySelectorAll<HTMLElement>('[data-reveal-stagger]').forEach((el) => {
        groups.push(Array.from(el.children) as HTMLElement[]);
      });

      groups.forEach((items) => {
        // One measurement at setup only; skip anything already on screen
        const pending = items.filter((el) => el.getBoundingClientRect().top > vh * 0.92);
        if (!pending.length) return;
        gsap.set(pending, { autoAlpha: 0, y: dist });
        ScrollTrigger.create({
          trigger: pending[0],
          start: 'top 88%',
          once: true,
          onEnter: () => {
            gsap.to(pending, {
              autoAlpha: 1,
              y: 0,
              duration: 0.6,
              ease: 'power2.out',
              stagger: 0.07,
              clearProps: 'opacity,visibility,transform',
            });
          },
        });
      });

      // Editorial image reveal: image only (parent container is static, no parallax)
      root.querySelectorAll<HTMLElement>('[data-reveal-image]').forEach((img) => {
        if (img.getBoundingClientRect().top <= vh * 0.92) return;
        gsap.set(img, { autoAlpha: 0, scale: coarse ? 1.02 : 1.04 });
        ScrollTrigger.create({
          trigger: img,
          start: 'top 90%',
          once: true,
          onEnter: () => {
            gsap.to(img, {
              autoAlpha: 1,
              scale: 1,
              duration: 0.7,
              ease: 'power3.out',
              clearProps: 'opacity,visibility,transform',
            });
          },
        });
      });
    }, root);

    return () => ctx.revert();
  }, [rootRef]);
}